import React from 'react';
import {
  Badge,
  Box,
  Chip,
  Collapse,
  Divider,
  Fab,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { notificationHtmlToText } from '../utils/formatters';
import {
  Close as CloseIcon,
  DeleteSweep as DeleteSweepIcon,
  Forum as ForumIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Notifications as NotificationsIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const DiscordAlertsWidget = ({ alerts, unreadCount, onClear, onMarkRead }) => {
  const [expanded, setExpanded] = React.useState(false);

  const handleToggle = () => {
    const nextExpanded = !expanded;
    setExpanded(nextExpanded);
    if (nextExpanded) onMarkRead();
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        right: { xs: 12, sm: 24 },
        bottom: { xs: 12, sm: 24 },
        zIndex: theme => theme.zIndex.tooltip + 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
    >
      <Collapse in={expanded} timeout={220} unmountOnExit>
        <Paper
          elevation={10}
          sx={{
            width: { xs: 'calc(100vw - 24px)', sm: 380 },
            maxWidth: 380,
            maxHeight: { xs: 'calc(100vh - 110px)', sm: 520 },
            mb: 1.5,
            overflow: 'hidden',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TrendingUpIcon fontSize="small" />
              <Typography variant="subtitle1" fontWeight={700}>Trade Alerts</Typography>
            </Stack>
            <Stack direction="row" spacing={0.25} alignItems="center">
              <Tooltip title="Clear alerts">
                <IconButton size="small" onClick={onClear} sx={{ color: 'inherit' }} disabled={!alerts.length}>
                  <DeleteSweepIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={handleToggle} sx={{ color: 'inherit' }}>
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          <Box sx={{ overflowY: 'auto', maxHeight: { xs: 'calc(100vh - 175px)', sm: 450 } }}>
            {alerts.length === 0 ? (
              <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                <ForumIcon color="disabled" sx={{ fontSize: 34, mb: 1 }} />
                <Typography variant="body2" color="text.secondary">No alerts yet</Typography>
              </Box>
            ) : (
              alerts.map((alert, index) => (
                <React.Fragment key={alert.id}>
                  <Box sx={{ px: 2, py: 1.25 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Box sx={{ color: alert.type === 'trade_alert' ? 'success.main' : alert.type === 'price_alert' ? 'warning.main' : 'info.main', pt: 0.25 }}>
                        {alert.type === 'trade_alert' ? <TrendingUpIcon fontSize="small" /> : alert.type === 'price_alert' ? <NotificationsIcon fontSize="small" /> : <ForumIcon fontSize="small" />}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>{alert.title || 'Notification'}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', flexShrink: 0 }}>{formatTime(alert.timestamp)}</Typography>
                        </Stack>
                        {alert.channelName && <Chip label={`#${alert.channelName}`} size="small" variant="outlined" sx={{ mb: 0.5, height: 20, fontSize: '0.68rem' }} />}
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {notificationHtmlToText(alert.body) || 'New notification'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                  {index < alerts.length - 1 && <Divider />}
                </React.Fragment>
              ))
            )}
          </Box>
        </Paper>
      </Collapse>

      <Tooltip title={expanded ? 'Collapse alerts' : 'Open trade alerts'} placement="left">
        <Fab color="primary" size="medium" onClick={handleToggle} aria-label="Discord trade alerts">
          <Badge badgeContent={unreadCount > 99 ? '99+' : unreadCount} color="error" overlap="circular">
            {expanded ? <CloseIcon /> : <NotificationsIcon />}
          </Badge>
        </Fab>
      </Tooltip>
    </Box>
  );
};

export default DiscordAlertsWidget;
